import * as cheerio from "cheerio"

export const INTERNAL_ARTICLE_LINK_CLASS = "docs-inline-article-link"
export const INTERNAL_ARTICLE_LINK_ICON_CLASS = "docs-inline-article-link-icon"
const LINK_TITLE_SELECTOR = "[data-card-title], [data-tile-title]"

// Keyed by both urlName and article id, since the editor's "@" picker inserts
// the id-based href that survives renames.
export interface InternalArticleLinkTarget {
	urlName?: string
	icon?: string | null
}

interface ResolveOptions {
	portalPrefix?: string
	hosts?: string[]
}

const stripTrailingSlash = (path: string) =>
	path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path

const normalizeHost = (host: string) =>
	host
		.toLowerCase()
		.replace(/^www\./, "")
		.replace(/:80$/, "")
		.replace(/:443$/, "")

const splitHref = (
	href: string,
	hosts: string[],
): { pathname: string; hash: string } | null => {
	if (href.startsWith("/") && !href.startsWith("//")) {
		const hashIndex = href.indexOf("#")
		const hash = hashIndex >= 0 ? href.slice(hashIndex) : ""
		const withoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href
		return { pathname: withoutHash.split("?")[0] ?? "", hash }
	}
	try {
		const url = new URL(href)
		if (url.protocol !== "http:" && url.protocol !== "https:") return null
		const allowed = hosts.filter(Boolean).map(normalizeHost)
		if (!allowed.includes(normalizeHost(url.host))) return null
		return { pathname: url.pathname, hash: url.hash }
	} catch {
		return null
	}
}

// Returns the docs path segment an href points at (a urlName or an article id),
// or null when the link leaves the help center.
export const parseInternalArticleLink = (
	href: string | undefined | null,
	{ portalPrefix = "", hosts = [] }: ResolveOptions = {},
): { segment: string; hash: string } | null => {
	if (!href) return null

	const parts = splitHref(href, hosts)
	if (!parts) return null

	let path = stripTrailingSlash(parts.pathname)
	if (portalPrefix && path.startsWith(`${portalPrefix}/`)) {
		path = path.slice(portalPrefix.length)
	}
	if (!path.startsWith("/docs/")) return null

	const raw = path.slice("/docs/".length)
	if (!raw) return null

	let segment = raw
	try {
		segment = decodeURIComponent(raw)
	} catch {
		// a malformed escape sequence stays as written
	}

	return { segment, hash: parts.hash }
}

export const resolveInternalArticleSegment = (
	href: string | undefined | null,
	options: ResolveOptions = {},
): string | null => parseInternalArticleLink(href, options)?.segment ?? null

// Turns links that point at another article in the same help center into an
// inline pill: the target's icon, a gray background, and no target="_blank".
export const decorateInternalArticleLinks = (
	html: string,
	{
		articles,
		portalPrefix = "",
		hosts = [],
	}: ResolveOptions & { articles: Record<string, InternalArticleLinkTarget> },
): string => {
	if (!html) return html

	const $ = cheerio.load(html)
	let changed = false

	$("a[href]").each((_, element) => {
		const anchor = $(element)
		const link = parseInternalArticleLink(anchor.attr("href"), {
			portalPrefix,
			hosts,
		})
		if (!link) return

		const article = articles[link.segment]
		if (!article) return

		const urlName = article.urlName ?? link.segment

		changed = true
		anchor.removeAttr("target")
		anchor.removeAttr("rel")
		anchor.removeAttr("style")
		// Rewrite to the portal-relative path so the click routes client-side.
		anchor.attr("href", `${portalPrefix}/docs/${urlName}${link.hash}`)
		anchor.attr("data-doc-link", urlName)
		// A card or tile title is the component's own link, styled by the component.
		if (anchor.closest(LINK_TITLE_SELECTOR).length > 0) return
		anchor.attr("class", INTERNAL_ARTICLE_LINK_CLASS)

		if (
			article.icon &&
			anchor.children(`.${INTERNAL_ARTICLE_LINK_ICON_CLASS}`).length === 0
		) {
			anchor.prepend(
				$("<span></span>")
					.addClass(INTERNAL_ARTICLE_LINK_ICON_CLASS)
					.attr("data-doc-link-icon", article.icon),
			)
		}
	})

	return changed ? ($("body").html() ?? html) : html
}
